// Yandex Metrica ClientID capture — used to feed Yandex Direct Центр конверсий
// CSV (bd-glg). The `_ym_uid` cookie is set by the Metrica tag on first visit
// and is the identifier Direct's CSV `client_ids` column expects for matching
// conversions to visits. We cache it in localStorage so it survives reloads
// and is available on register/login (where it gets persisted server-side).
//
// Unlike invite/access tokens (cleared after use), yandex_client_id is NOT
// cleared — we keep sending whatever's currently in the cookie, so a user
// logging in on a new device/browser gets the new ClientID recorded.

const STORAGE_KEY = 'pwa-chat-ym-uid'
const COOKIE_NAME = '_ym_uid'

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;)\\s*${name}=([^;]+)`))
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Refresh the cached ClientID from the `_ym_uid` cookie. Called on app mount.
 * Returns the current ClientID (cookie value if present, else localStorage
 * fallback). Safe to call repeatedly — overwrites cache only when a fresh
 * cookie value is seen.
 */
export function detectYandexClientId(): string | null {
  const fromCookie = readCookie(COOKIE_NAME)
  if (fromCookie) {
    localStorage.setItem(STORAGE_KEY, fromCookie)
    return fromCookie
  }
  return localStorage.getItem(STORAGE_KEY)
}

export function getYandexClientId(): string | null {
  // Always re-check cookie first — the cookie can land after React mounts
  // (Metrica tag loads async), and we want the freshest value at the moment
  // of the auth call.
  return readCookie(COOKIE_NAME) || localStorage.getItem(STORAGE_KEY)
}

// --- async resolution + opportunistic backfill (bd-ttr) ---------------------
// The sync read above misses the ClientID ~79% of the time for email-form
// users because the async tag hasn't written `_ym_uid` yet. getClientID (via
// the tag) is resilient to that race. We resolve async when we can afford to
// wait (login complete, chat send) and POST the result once per session.

import { fetchClientIdViaTag } from './metrica'
import { authApi } from '../api/auth'

let _lastResolutionFailed = false // getClientID + cookie both empty → tag likely blocked

/**
 * Best async ClientID resolution: try the tag's getClientID, fall back to the
 * sync cookie/localStorage read. Sets the module-level failed flag when both
 * come up empty (used by the backfill to report a "blocked" event).
 */
export async function resolveYandexClientId(): Promise<string | null> {
  const viaTag = await fetchClientIdViaTag()
  if (viaTag) {
    _lastResolutionFailed = false
    localStorage.setItem(STORAGE_KEY, viaTag) // cache for sync callers
    return viaTag
  }
  const fallback = getYandexClientId()
  _lastResolutionFailed = !fallback
  return fallback
}

let _backfilled = false
let _blockedReported = false

/**
 * Opportunistic, idempotent-per-session backfill of the Yandex ClientID.
 *
 * Called from login-complete and chat-send (never blocks either). Resolves the
 * ClientID async and POSTs it to the backend, which only stores it if the user
 * doesn't already have one (backend no-op-if-set guard). Keeps the "catch it
 * whenever it appears" property that the single auth-moment read lacks.
 *
 * When resolution fails (getClientID never resolves + no cookie), reports a
 * one-time `blocked` flag so the backend logs the irreducible capture floor,
 * then stops retrying this session.
 */
export async function ensureClientIdBackfilled(): Promise<void> {
  if (_backfilled) return
  const ymUid = await resolveYandexClientId()
  if (ymUid) {
    try {
      await authApi.persistYandexClientId(ymUid)
      _backfilled = true
    } catch {
      // Network failure — leave _backfilled false so a later login/chat-send retries.
    }
    return
  }
  // ClientID unobtainable: Metrica is blocked or the tag never loaded.
  if (!_blockedReported) {
    _blockedReported = true
    authApi.persistYandexClientId('', true).catch(() => {})
  }
}

/** Test helper — reset module state between unit tests. */
export function _resetClientIdBackfillState(): void {
  _backfilled = false
  _blockedReported = false
  _lastResolutionFailed = false
}
